import React, { useEffect, useRef, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtINR } from "../../lib/constants";
import Paginated from "../../components/Paginated";
import DateInput from "../../components/DateInput";
import ImageUploader from "../../components/ImageUploader";
import { toast } from "sonner";
import { Send, Clock, Star, AlertTriangle, X, Plus, BadgeIndianRupee, ShieldBan, Paperclip } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/**
 * Astrologer Inbox.
 * — Astrologer: sets availability (weekly + specific dates), sees ONLY chats
 *   assigned to him (or unclaimed), replies with response-time tracking
 *   (>30 min replies are marked late), sees earnings/outstanding & payout logs.
 * — Super Admin: read-only — full chat logs, premium payments (payment ID,
 *   time, who, amount, channel), per-astrologer commission & payouts, and the
 *   banned-keywords manager. Cannot reply to anyone.
 * Both views auto-refresh every 10 seconds.
 */
export default function AstrologerInbox() {
  const { user } = useAuth();
  // A super admin who is ALSO an astrologer can reply to their own chats, so is not read-only.
  const isSuper = hasRole(user, "super_admin") && !hasRole(user, "astrologer");
  const isAstrologer = hasRole(user, "astrologer");

  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [liveStatus, setLiveStatus] = useState(null);
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  // Gemstone attachment (astrologer only)
  const [gemOpen, setGemOpen] = useState(false);
  const [gems, setGems] = useState([]);
  const [gemCats, setGemCats] = useState([]);
  const [gemFilter, setGemFilter] = useState("all");
  const [gemSel, setGemSel] = useState({}); // product_id -> {name,image,carat,category,caption,wear_metal}
  const loadGems = (cat = "all") => api.get(`/api/crm/astrology/gemstones${cat && cat !== "all" ? `?category=${encodeURIComponent(cat)}` : ""}`)
    .then((r) => { setGems(r.data?.gemstones || []); setGemCats(r.data?.categories || []); }).catch(() => {});
  const toggleGem = (g) => setGemSel((prev) => {
    const n = { ...prev };
    if (n[g.product_id]) delete n[g.product_id];
    else n[g.product_id] = { name: g.name, image: g.image, carat: g.carat, category: g.category, product_id: g.product_id, caption: "", wear_metal: "None" };
    return n;
  });
  const setGemField = (pid, field, val) => setGemSel((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: val } }));
  const attachGems = async () => {
    const list = Object.values(gemSel);
    if (list.length === 0) { toast.error("Select at least one gemstone"); return; }
    try {
      await api.post("/api/astrology/message", {
        chat_id: active.id, sender: "astrologer",
        message: reply.trim() || "Suggested gemstone(s) for you:",
        attachments: list,
      });
      setReply(""); setGemSel({}); setGemOpen(false);
      loadMessages(active.id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not attach"); }
  };

  const [search, setSearch] = useState({ q: "", date: "" });
  const searchRef = useRef(search);
  searchRef.current = search;
  const loadChats = () => {
    const qp = new URLSearchParams();
    if (searchRef.current.q) qp.set("q", searchRef.current.q);
    if (searchRef.current.date) qp.set("date", searchRef.current.date);
    return api.get(`/api/crm/astrology/chats?${qp}`).then((r) => setChats(r.data || [])).catch(() => {});
  };
  const loadLive = () => api.get("/api/astrology/live-status").then((r) => setLiveStatus(r.data)).catch(() => {});
  const loadMessages = (chatId) => api.get(`/api/astrology/chat/${chatId}`).then((r) => setMessages(r.data.messages || []));

  // Both pages refresh every 10 seconds
  useEffect(() => {
    loadChats(); loadLive();
    const t = setInterval(() => { loadChats(); loadLive(); if (active) loadMessages(active.id); }, 10000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [active?.id]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  // Search: word inside chats, customer name, user ID, or date
  useEffect(() => {
    const t = setTimeout(loadChats, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search.q, search.date]);

  const openChat = async (c) => {
    setActive(c);
    if (isAstrologer && !c.astrologer_id) {
      await api.post(`/api/crm/astrology/chats/${c.id}/claim`).catch(() => {});
      loadChats();
    }
    loadMessages(c.id);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    try {
      const { data } = await api.post("/api/astrology/message", { chat_id: active.id, sender: "astrologer", message: reply });
      if (data.late) toast.warning(`Marked late — this reply took ${data.response_minutes} minutes (>30 min)`);
      setReply("");
      loadMessages(active.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    }
  };

  // Response-time mark for a message
  const RespMark = ({ m }) =>
    m.sender === "astrologer" && m.response_minutes != null ? (
      <span className={`text-[9px] ml-2 ${m.late ? "text-red-600 font-semibold" : "text-ash"}`} title="Time taken to reply">
        <Clock size={9} className="inline mr-0.5"/>{m.response_minutes} min{m.late && " · LATE"}
      </span>
    ) : null;

  return (
    <div className="p-6 lg:p-8 space-y-8">
      <div>
        <div className="eyebrow">Live Guidance</div>
        <h1 className="font-display text-4xl text-maroon">Astrologer {isSuper ? "Logs & Payments" : "Inbox"}</h1>
        {isSuper && <p className="text-xs text-ash mt-1">Read-only view — you can see every conversation and payment, but cannot reply.</p>}
      </div>

      {/* No-astrologer-live popup banner (shown to every astrologer) */}
      {isAstrologer && liveStatus && !liveStatus.any_live && (
        <div className="bg-gold/15 border-2 border-gold p-4 flex items-start gap-3" data-testid="no-live-popup">
          <AlertTriangle size={20} className="text-gold-dark shrink-0 mt-0.5"/>
          <div>
            <div className="font-display text-xl text-maroon">Currently no astrologer is live!</div>
            <div className="text-sm text-ash">If you take this opportunity and go live now, you can earn more — set your available time below.</div>
          </div>
        </div>
      )}

      {isAstrologer && <AvailabilityEditor/>}
      {isAstrologer && <MyEarnings/>}

      {/* Chats + conversation */}
      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-1">
          <div className="eyebrow mb-3">{isSuper ? `All Chats (${chats.length})` : `My Customers / Chats (${chats.length})`}</div>
          <div className="space-y-2 mb-3" data-testid="chat-search">
            <input
              placeholder="Search — word in chats, customer name, or user ID…"
              className="w-full border border-line px-3 py-2 bg-white text-sm"
              value={search.q}
              onChange={(e) => setSearch({ ...search, q: e.target.value })}
              data-testid="chat-search-q"
            />
            <div className="flex items-center gap-2">
              <DateInput className="flex-1" yearsBack={2} yearsAhead={0} value={search.date} onChange={(v) => setSearch({ ...search, date: v })} data-testid="chat-search-date"/>
              {(search.q || search.date) && <button onClick={() => setSearch({ q: "", date: "" })} className="text-xs text-maroon underline" data-testid="chat-search-clear">Clear</button>}
            </div>
          </div>
          <Paginated items={chats} pageSize={8}>
            {(pageItems) => (
              <div className="space-y-2">
                {pageItems.map((c) => (
                  <button key={c.id} onClick={() => openChat(c)}
                    className={`w-full text-left bg-white border p-3 ${active?.id === c.id ? "border-maroon" : "border-line"}`}
                    data-testid={`chat-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">{c.customer_name}</div>
                      <div className="flex items-center gap-1">
                        {c.rating && <span className="text-[10px] text-gold-dark"><Star size={9} className="inline fill-gold stroke-gold"/> {c.rating}</span>}
                        {c.is_paid && <span className="bg-green-100 text-green-800 text-[8px] px-1 uppercase tracking-widest">Paid</span>}
                      </div>
                    </div>
                    <div className="text-[10px] text-ash">
                      ID {c.user_id || c.user_id_short || c.id.slice(0, 8).toUpperCase()}{isSuper && c.customer_phone ? ` · ${c.customer_phone}` : ""} · {new Date(c.created_at).toLocaleString()}
                    </div>
                    {isSuper && <div className="text-[10px] text-maroon mt-0.5">Astrologer: {c.astrologer_name || "Unclaimed"}</div>}
                  </button>
                ))}
                {chats.length === 0 && <div className="text-xs text-ash p-6 text-center bg-white border border-line">No chats yet.</div>}
              </div>
            )}
          </Paginated>
        </section>

        <section className="lg:col-span-2">
          <div className="eyebrow mb-3">Conversation</div>
          <div className="bg-white border border-line flex flex-col h-[480px]">
            {!active ? <div className="flex-1 flex items-center justify-center text-ash text-sm">Select a chat</div> : (
              <>
                <div className="p-3 border-b border-line flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{active.customer_name} <span className="text-xs text-ash">· ID {active.user_id || active.user_id_short || active.id.slice(0, 8).toUpperCase()}{isSuper && active.customer_phone ? ` · ${active.customer_phone}` : ""}</span></div>
                    {(active.dob || active.birth_time || active.birthplace || active.zodiac) && (
                      <div className="text-[10px] text-maroon mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        {active.zodiac && <span>♦ Rashi: <b>{active.zodiac}</b></span>}
                        {active.dob && <span>DOB: {active.dob}</span>}
                        {active.birth_time && <span>Time: {active.birth_time}</span>}
                        {active.birthplace && <span>Place: {active.birthplace}</span>}
                      </div>
                    )}
                    <div className="text-[10px] text-ash">Free words: {active.free_words_used}/{active.free_words_limit} {active.is_paid && "· PREMIUM UNLOCKED"}</div>
                  </div>
                  {active.payment_id && <div className="text-[10px] text-green-700">Payment: {active.payment_id}</div>}
                </div>
                <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((m) => (
                    <div key={m.id} className={`max-w-[80%] ${m.sender === "astrologer" ? "ml-auto" : ""}`}>
                      <div className={`p-2.5 text-sm ${m.sender === "astrologer" ? "bg-maroon text-pearl" : "bg-pearl border border-line"}`}>
                        {m.image_url && <img src={m.image_url} alt="" className="max-h-40 mb-1 border border-line"/>}
                        {m.image_expired && <div className="text-[9px] italic opacity-70 mb-1">📷 Image expired (chat images are kept for 7 days only)</div>}
                        {m.message}
                        {(m.attachments || []).length > 0 && (
                          <div className="mt-2 space-y-2">
                            {m.attachments.map((g, gi) => (
                              <div key={gi} className="bg-white text-ink border border-gold rounded-lg p-2 flex gap-2">
                                {g.image && <img src={g.image} alt={g.name} className="w-12 h-12 object-cover rounded shrink-0"/>}
                                <div className="min-w-0">
                                  <div className="font-medium text-maroon text-[13px]">{g.name}{g.carat ? ` · ${g.carat}ct` : ""}</div>
                                  {g.wear_metal && g.wear_metal !== "None" && <div className="text-[10px] text-ash">Wear in: <b>{g.wear_metal}</b></div>}
                                  {g.caption && <div className="text-[11px] mt-0.5">{g.caption}</div>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-[9px] text-ash mt-0.5">
                        {m.sender === "customer" ? "Asked" : "Answered"} at {new Date(m.created_at).toLocaleTimeString()}
                        <RespMark m={m}/>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef}/>
                </div>
                {isAstrologer ? (
                  <form onSubmit={sendReply} className="p-3 border-t border-line flex gap-2">
                    <button type="button" onClick={() => { setGemOpen(true); loadGems(gemFilter); }} className="border border-line px-3 text-ash hover:text-maroon" title="Attach gemstones" data-testid="astro-attach-gem"><Paperclip size={16}/></button>
                    <input className="flex-1 border border-line px-3 py-2 bg-pearl text-sm" placeholder="Type your guidance…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="astro-reply-input"/>
                    <button className="btn-maroon" data-testid="astro-reply-send"><Send size={14}/></button>
                  </form>
                ) : (
                  <div className="p-3 border-t border-line text-center text-[11px] text-ash bg-pearl" data-testid="readonly-note">
                    View-only — Super Admin cannot reply to any conversation.
                  </div>
                )}

                {/* Gemstone attach modal */}
                {gemOpen && (
                  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setGemOpen(false)}>
                    <div className="bg-white border border-line w-full max-w-2xl max-h-[88vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="gem-attach-modal">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-display text-2xl text-maroon">Attach gemstones</div>
                        <button onClick={() => setGemOpen(false)}><X size={18}/></button>
                      </div>
                      {/* Category filter */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {["all", ...gemCats].map((c) => (
                          <button key={c} onClick={() => { setGemFilter(c); loadGems(c); }} className={`text-xs px-3 py-1 rounded-full border ${gemFilter === c ? "bg-maroon text-pearl border-maroon" : "bg-white border-line"}`} data-testid={`gem-cat-${c}`}>{c === "all" ? "All" : c}</button>
                        ))}
                      </div>
                      {/* Gemstone list */}
                      <div className="grid sm:grid-cols-2 gap-2">
                        {gems.map((g) => {
                          const sel = gemSel[g.product_id];
                          return (
                            <div key={g.product_id} className={`border p-2 rounded ${sel ? "border-maroon bg-gold/10" : "border-line"}`} data-testid={`gem-item-${g.product_id}`}>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={!!sel} onChange={() => toggleGem(g)}/>
                                {g.image && <img src={g.image} alt={g.name} className="w-10 h-10 object-cover rounded"/>}
                                <div className="min-w-0"><div className="text-sm font-medium truncate">{g.name}</div><div className="text-[10px] text-ash">{g.category}{g.carat ? ` · ${g.carat}ct` : ""}</div></div>
                              </label>
                              {sel && (
                                <div className="mt-2 space-y-1.5">
                                  <input placeholder="Caption (e.g. wear on right ring finger)" className="w-full border border-line px-2 py-1 text-xs bg-pearl" value={sel.caption} onChange={(e) => setGemField(g.product_id, "caption", e.target.value)} data-testid={`gem-caption-${g.product_id}`}/>
                                  <select className="w-full border border-line px-2 py-1 text-xs bg-pearl" value={sel.wear_metal} onChange={(e) => setGemField(g.product_id, "wear_metal", e.target.value)} data-testid={`gem-metal-${g.product_id}`}>
                                    {["None", "Gold", "Silver", "Platinum"].map((mtl) => <option key={mtl} value={mtl}>{mtl === "None" ? "No metal / as-is" : `Wear in ${mtl}`}</option>)}
                                  </select>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {gems.length === 0 && <div className="text-sm text-ash col-span-2 text-center py-6">No gemstones in the catalogue for this filter.</div>}
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-xs text-ash">{Object.keys(gemSel).length} selected</div>
                        <button onClick={attachGems} className="btn-maroon" data-testid="gem-attach-submit"><Paperclip size={14}/> Attach to consultation</button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Super Admin: payments, per-astrologer commission & payouts, banned keywords */}
      {isSuper && <PaymentsAndPayouts/>}
      {isSuper && <BannedKeywords/>}
    </div>
  );
}

/* ============ Availability (weekly + specific dates) ============ */
function AvailabilityEditor() {
  const [slots, setSlots] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [nf, setNf] = useState({ type: "weekly", day: 0, date: "", from: "10:00", to: "13:00" });

  const load = () => api.get("/api/crm/astrology/availability").then((r) => { setSlots(r.data.slots || []); setIsLive(!!r.data.is_live_now); });
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    const { data } = await api.post("/api/crm/astrology/availability", { slots: next });
    setSlots(data.slots || []); setIsLive(!!data.is_live_now);
    toast.success("Availability updated");
  };

  const add = (e) => {
    e.preventDefault();
    if (nf.type === "date" && !nf.date) { toast.error("Pick the date"); return; }
    if (!nf.from || !nf.to || nf.from >= nf.to) { toast.error("Give a valid time range"); return; }
    save([...slots, { type: nf.type, day: nf.type === "weekly" ? +nf.day : null, date: nf.type === "date" ? nf.date : null, from: nf.from, to: nf.to }]);
  };
  const remove = (i) => save(slots.filter((_, idx) => idx !== i));

  return (
    <section className="bg-white border border-line p-5" data-testid="availability-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">My Availability (when I'm live)</div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${isLive ? "bg-green-100 text-green-800" : "bg-line text-ash"}`} data-testid="live-badge">
          {isLive ? "● You are LIVE now" : "○ Not live now"}
        </span>
      </div>
      <p className="text-xs text-ash mb-3">Add multiple slots per day — e.g. Monday 10 AM–1 PM and 4 PM–7 PM, or a specific date like 8 July 10 AM–4 PM and 8 PM–10 PM.</p>
      <form onSubmit={add} className="flex flex-wrap items-end gap-2 mb-4">
        <select className="border border-line px-2 py-2 bg-pearl text-sm" value={nf.type} onChange={(e) => setNf({...nf, type: e.target.value})} data-testid="slot-type">
          <option value="weekly">Every week</option>
          <option value="date">Specific date</option>
        </select>
        {nf.type === "weekly" ? (
          <select className="border border-line px-2 py-2 bg-pearl text-sm" value={nf.day} onChange={(e) => setNf({...nf, day: e.target.value})} data-testid="slot-day">
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        ) : (
          <DateInput value={nf.date} onChange={(v) => setNf({...nf, date: v})} yearsBack={0} yearsAhead={1} data-testid="slot-date"/>
        )}
        <label className="text-xs">From <input type="time" className="border border-line px-2 py-1.5 bg-pearl block" value={nf.from} onChange={(e) => setNf({...nf, from: e.target.value})} data-testid="slot-from"/></label>
        <label className="text-xs">To <input type="time" className="border border-line px-2 py-1.5 bg-pearl block" value={nf.to} onChange={(e) => setNf({...nf, to: e.target.value})} data-testid="slot-to"/></label>
        <button className="btn-maroon text-xs" data-testid="slot-add"><Plus size={12}/> Add Slot</button>
      </form>
      <div className="flex flex-wrap gap-2">
        {slots.map((s, i) => (
          <span key={i} className="inline-flex items-center gap-2 border border-line px-2 py-1 text-xs bg-pearl" data-testid={`slot-${i}`}>
            {s.type === "weekly" ? DAYS[s.day] : new Date(s.date).toLocaleDateString("en-GB")} · {s.from}–{s.to}
            <button onClick={() => remove(i)} className="text-ash hover:text-red-600" data-testid={`slot-del-${i}`}><X size={11}/></button>
          </span>
        ))}
        {slots.length === 0 && <span className="text-xs text-ash">No slots yet — you appear offline to customers until you add one.</span>}
      </div>
    </section>
  );
}

/* ============ Astrologer: my earnings & payout logs ============ */
function MyEarnings() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/api/crm/astrology/earnings").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const rev = data.revenue || {};
  return (
    <section className="bg-white border border-line p-5" data-testid="my-earnings">
      <div className="eyebrow mb-3">My Earnings (after Swayambhu Gold's {data.commission_pct}% commission)</div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 text-center">
        {[["Today", rev.day], ["This Week", rev.week], ["This Month", rev.month], ["6 Months", rev.six_months], ["This Year", rev.year], ["Lifetime", rev.total]].map(([l, v]) => (
          <div key={l} className="border border-line p-3">
            <div className="font-display text-lg text-maroon">{fmtINR(v || 0)}</div>
            <div className="text-[9px] uppercase tracking-widest text-ash">{l}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 items-center">
        <div className="text-sm">Outstanding (to be paid by Swayambhu Gold at month-end): <b className="text-maroon">{fmtINR(data.outstanding || 0)}</b></div>
        <div className="text-xs text-ash">Paid till now: {fmtINR(data.total_paid_out || 0)}</div>
      </div>
      {(data.payout_logs || []).length > 0 && (
        <div className="mt-4">
          <div className="eyebrow text-[10px] mb-2">Payout Logs</div>
          <Paginated items={data.payout_logs} pageSize={5}>
            {(pageItems) => pageItems.map((p) => (
              <div key={p.id} className="border border-line p-2 text-xs flex flex-wrap justify-between mb-1" data-testid={`payout-${p.id}`}>
                <span>{new Date(p.paid_at).toLocaleDateString("en-GB")} · {p.method} {p.reference && `· ${p.reference}`}</span>
                <b className="text-maroon">{fmtINR(p.amount)}</b>
              </div>
            ))}
          </Paginated>
        </div>
      )}
    </section>
  );
}

/* ============ Super Admin: payments, commission & payouts ============ */
function PaymentsAndPayouts() {
  const [payments, setPayments] = useState([]);
  const [astros, setAstros] = useState([]);
  const [payoutFor, setPayoutFor] = useState(null); // astrologer summary object
  const [pf, setPf] = useState({ amount: 0, method: "UPI", reference: "", paid_at: "", proof_images: [], note: "" });

  const load = () => {
    api.get("/api/crm/astrology/payments").then((r) => setPayments(r.data || []));
    api.get("/api/crm/astrology/earnings").then((r) => setAstros(r.data?.astrologers || []));
  };
  useEffect(() => { load(); }, []);

  const openPayout = (a) => { setPayoutFor(a); setPf({ amount: a.outstanding || 0, method: "UPI", reference: "", paid_at: "", proof_images: [], note: "" }); };
  const submitPayout = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/crm/astrology/payouts", { ...pf, astrologer_id: payoutFor.user_id, paid_at: pf.paid_at || undefined });
      toast.success("Payout recorded — outstanding is now reset");
      setPayoutFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveCommission = async (a, pct) => {
    await api.patch(`/api/crm/users/${a.user_id}`, { commission_pct: +pct });
    toast.success(`Commission for ${a.name} set to ${pct}%`);
    load();
  };

  return (
    <>
      {/* Per-astrologer commission + payable */}
      <section data-testid="astro-payables">
        <div className="eyebrow mb-3">Astrologer Payables (commission can differ per astrologer)</div>
        <div className="bg-white border border-line overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-pearl border-b border-line">
              <tr>{["Astrologer", "Chats", "Premium", "Gross Collected", "Commission %", "Payable (share)", "Paid", "Outstanding", ""].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {astros.map((a) => (
                <tr key={a.user_id} className="border-b border-line/60" data-testid={`payable-${a.user_id}`}>
                  <td className="p-3 font-medium">{a.name}</td>
                  <td className="p-3">{a.chats?.total ?? 0}</td>
                  <td className="p-3">{a.premium_chats ?? 0}</td>
                  <td className="p-3">{fmtINR(a.gross_collected || 0)}</td>
                  <td className="p-3">
                    <input type="number" defaultValue={a.commission_pct} onBlur={(e) => +e.target.value !== a.commission_pct && saveCommission(a, e.target.value)}
                      className="w-16 border border-line px-2 py-1 bg-pearl text-sm" data-testid={`commission-${a.user_id}`}/>%
                  </td>
                  <td className="p-3">{fmtINR(a.revenue?.total || 0)}</td>
                  <td className="p-3">{fmtINR(a.total_paid_out || 0)}</td>
                  <td className="p-3 font-semibold text-maroon">{fmtINR(a.outstanding || 0)}</td>
                  <td className="p-3">
                    <button onClick={() => openPayout(a)} className="btn-gold text-xs" disabled={(a.outstanding || 0) <= 0} data-testid={`payout-btn-${a.user_id}`}>
                      <BadgeIndianRupee size={12}/> Pay Out
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {astros.length === 0 && <div className="p-8 text-center text-ash text-sm">No astrologers yet.</div>}
        </div>
      </section>

      {/* Premium chat payments */}
      <section data-testid="astro-payments">
        <div className="eyebrow mb-3">Premium Chat Payments (after 60 free words)</div>
        <Paginated items={payments} pageSize={10}>
          {(pageItems) => (
            <div className="bg-white border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-pearl border-b border-line">
                  <tr>{["When", "From (customer)", "Phone", "Astrologer", "Payment ID", "Channel", "Amount"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {pageItems.map((p) => (
                    <tr key={p.id} className="border-b border-line/60" data-testid={`astropay-${p.id}`}>
                      <td className="p-3 text-xs">{new Date(p.paid_at).toLocaleString()}</td>
                      <td className="p-3">{p.customer_name}</td>
                      <td className="p-3 text-xs text-ash">{p.customer_phone}</td>
                      <td className="p-3 text-xs">{p.astrologer_name || "—"}</td>
                      <td className="p-3 text-xs">{p.payment_id}</td>
                      <td className="p-3 text-xs uppercase">{p.method || "razorpay"}</td>
                      <td className="p-3 font-medium text-maroon">{fmtINR(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payments.length === 0 && <div className="p-8 text-center text-ash text-sm">No premium payments yet.</div>}
            </div>
          )}
        </Paginated>
      </section>

      {/* Payout modal */}
      {payoutFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPayoutFor(null)}>
          <form onSubmit={submitPayout} className="bg-white border border-line p-6 w-full max-w-md space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl text-maroon">Pay {payoutFor.name}</div>
            <div className="text-xs text-ash">Outstanding: {fmtINR(payoutFor.outstanding || 0)} (share after {payoutFor.commission_pct}% commission). After this payout the outstanding resets to zero.</div>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Amount (₹)</span>
              <input type="number" required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={pf.amount} onChange={(e) => setPf({...pf, amount: +e.target.value})} data-testid="payout-amount"/>
            </label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Payment type</span>
              <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={pf.method} onChange={(e) => setPf({...pf, method: e.target.value})} data-testid="payout-method">
                {["UPI", "Bank Transfer", "Cheque", "Cash"].map((m) => <option key={m}>{m}</option>)}
              </select>
            </label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Reference ({pf.method === "Cheque" ? "cheque number, bank, date on cheque" : "UPI ref / transaction ID"})</span>
              <input className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={pf.reference} onChange={(e) => setPf({...pf, reference: e.target.value})} data-testid="payout-reference"/>
            </label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Payment date</span>
              <DateInput className="mt-1" yearsBack={1} yearsAhead={0} value={pf.paid_at ? pf.paid_at.slice(0, 10) : ""} onChange={(v) => setPf({...pf, paid_at: v})} data-testid="payout-date"/>
            </label>
            <div>
              <div className="eyebrow text-[10px] mb-1">Proof {pf.method === "UPI" ? "(UPI screenshot)" : pf.method === "Cheque" ? "(cheque photo)" : "(optional)"}</div>
              <ImageUploader value={pf.proof_images} onChange={(urls) => setPf({...pf, proof_images: urls})} kind="payout" maxFiles={3}/>
            </div>
            <button className="btn-maroon w-full justify-center" data-testid="payout-submit">Record Payout</button>
          </form>
        </div>
      )}
    </>
  );
}

/* ============ Super Admin / Major Admin: banned chat keywords ============ */
function BannedKeywords() {
  const [words, setWords] = useState([]);
  const [word, setWord] = useState("");
  const load = () => api.get("/api/crm/banned-keywords").then((r) => setWords(r.data || []));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!word.trim()) return;
    try { await api.post("/api/crm/banned-keywords", { word }); setWord(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (w) => { await api.delete(`/api/crm/banned-keywords/${w.id}`); load(); };
  return (
    <section className="bg-white border border-line p-5" data-testid="banned-keywords">
      <div className="eyebrow mb-2 flex items-center gap-2"><ShieldBan size={14}/> Banned Chat Keywords</div>
      <p className="text-xs text-ash mb-3">If any of these words appears in a customer–astrologer message, the message is blocked from sending and the specific word is shown as not allowed.</p>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <input placeholder="Add a word to ban…" className="border border-line px-3 py-2 bg-pearl text-sm flex-1 max-w-xs" value={word} onChange={(e) => setWord(e.target.value)} data-testid="banned-input"/>
        <button className="btn-maroon text-xs" data-testid="banned-add">Ban Word</button>
      </form>
      <div className="flex flex-wrap gap-1.5">
        {words.map((w) => (
          <span key={w.id} className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-800 px-2 py-0.5 text-xs" data-testid={`banned-${w.id}`}>
            {w.word}
            <button onClick={() => remove(w)} className="hover:text-red-600" data-testid={`banned-del-${w.id}`}><X size={10}/></button>
          </span>
        ))}
        {words.length === 0 && <span className="text-xs text-ash">No banned words yet.</span>}
      </div>
    </section>
  );
}
